<template>
    <section
        class="step"
        :class="[bottomBackgroundIsPattern]"
        :style="{
            ...generateButtonVariables(step),
            ...generateInputVariables(step),
            ...generateContainerVariables(step),
        }"
    >
        <zoe-organism-banner
            v-if="getValue(`${step}.banner.show`)"
            :content="get(`${step}.banner`)"
        />

        <step-header
            v-if="getValue(`${step}.header.show`)"
            :content="get(`${step}.header`)"
        />

        <step-information
            v-if="getValue(`${step}.information-1.show`)"
            :content="get(`${step}.information-1`)"
            :identifier="1"
        />

        <step-media-blocks
            v-if="getValue(`${step}.mediaBlocks-1.show`)"
            :content="get(`${step}.mediaBlocks-1`)"
        />

        <div class="step__flow">
            <div class="step__flow-container">
                <Transition
                    name="step-transition"
                    @before-leave="beforeLeave"
                    @enter="onEnter"
                >
                    <step-start-cta
                        v-if="getValue(`${step}.startCta.show`) && $store.state.page.step === startStep"
                        :content="get(`${step}.startCta`)"
                        :step="step"
                        :show-value="getValue(`${step}.startCta.show`)"
                        @handleButtonClick="toggleOverlay"
                    />
                    <step-questions
                        v-else-if="$store.state.page.step === quizStep"
                        :content="get(`${step}.quiz`)"
                        :step="step"
                        :container-desktop-only="true"
                        @toggleOverlay="toggleOverlay"
                    />
                    <step-form
                        v-else-if="$store.state.page.step === formStep"
                        :content="get(`${step}.form`)"
                        :container-desktop-only="true"
                    />
                </Transition>
            </div>
        </div>

        <step-highlight-image
            v-if="getValue(`${step}.highlightImage.show`)"
            :content="get(`${step}.highlightImage`)"
        />

        <step-media-blocks
            v-if="getValue(`${step}.mediaBlocks-2.show`)"
            :content="get(`${step}.mediaBlocks-2`)"
        />

        <step-information
            v-if="getValue(`${step}.information-2.show`)"
            :content="get(`${step}.information-2`)"
            :identifier="2"
        />

        <step-information
            v-if="getValue(`${step}.information-3.show`)"
            :content="get(`${step}.information-3`)"
            :identifier="3"
        />

        <step-gallery
            v-if="getValue(`${step}.gallery.show`)"
            :content="get(`${step}.gallery`)"
        />

        <step-button-group-image
            v-if="getValue(`${step}.buttonGroupImage.show`)"
            :content="get(`${step}.buttonGroupImage`)"
        />

        <step-information
            v-if="getValue(`${step}.information-4.show`)"
            :content="get(`${step}.information-4`)"
            :identifier="4"
        />

        <step-information
            v-if="getValue(`${step}.information-5.show`)"
            :content="get(`${step}.information-5`)"
            :identifier="5"
        />

        <step-information
            v-if="getValue(`${step}.information-6.show`)"
            :content="get(`${step}.information-6`)"
            :identifier="6"
        />

        <step-information
            v-if="getValue(`${step}.information-7.show`)"
            :content="get(`${step}.information-7`)"
            :identifier="7"
        />

        <div class="step__section">
            <zoe-organism-usps
                v-if="getValue(`${step}.usps.show`)"
                :content="get(`${step}.usps`)"
            />
        </div>

        <step-testimonials
            v-if="getValue(`${step}.testimonials.show`)"
            :content="get(`${step}.testimonials`)"
        />

        <step-information
            v-if="getValue(`${step}.information-8.show`)"
            :content="get(`${step}.information-8`)"
            :identifier="8"
        />

        <step-start-cta
            v-if="(getValue(`${step}.startCta.showInFooter`) && !getValue(`${step}.startCta.show`)) || (getValue(`${step}.startCta.showInFooter`) && getValue(`${step}.startCta.show`) && $store.state.page.step === 1)"
            :content="get(`${step}.startCta`)"
            :step="step"
            :footer="true"
            :show-value="getValue(`${step}.startCta.show`)"
            @handleButtonClick="toggleOverlay"
        />

        <step-faq
            v-if="getValue(`${step}.faq.show`)"
            :content="get(`${step}.faq`)"
        />

        <zoe-organism-disclaimer
            v-if="getValue(`${step}.disclaimer.show`)"
            :content="get(`${step}.disclaimer`)"
        />
    </section>

    <Transition name="form-container-transition">
        <step-overlay
            v-if="flowHasStarted || getValue(`${step}.overlay.preview`)"
            :content="get(`${step}.overlay`)"
            :step="step"
            :quiz-step="quizStep"
            :form-step="formStep"
            @close-overlay="toggleOverlay"
            @overlay-preview-is-active="previewOverlay"
        />
    </Transition>
</template>

<script>
import ZoeAtomMedia from '@atoms/media/v1.0.0/zoe-atom-media.vue';
// json5-ignore
import ZoeOrganismBanner from '@organisms/banner/variation-1/v1.0.0/zoe-organism-banner.vue';
// json5-ignore
import StepHeader from '../v2.0.0/components/header/step-header.vue';
// json5-ignore
import StepInformation from './components/information/step-information.vue';
// json5-ignore
import StepMediaBlocks from '../v2.0.0/components/media-blocks/step-media-blocks.vue';
// json5-ignore
import StepStartCta from '../v2.0.0/components/start-cta/step-start-cta.vue';
// json5-ignore
import StepOverlay from '../v2.0.0/components/overlay/step-overlay.vue';
// json5-ignore
import StepQuestions from '../v2.0.0/components/questions/step-questions.vue';
// json5-ignore
import StepForm from '../v2.0.0/components/form/step-form.vue';
// json5-ignore
import StepHighlightImage from '../v2.0.0/components/highlight-image/step-highlight-image.vue';
// json5-ignore
import StepGallery from '../v2.0.0/components/gallery/step-gallery.vue';
// json5-ignore
import StepButtonGroupImage from '../v2.0.0/components/button-group-image/step-button-group-image.vue';
// json5-ignore
import ZoeOrganismUsps from '@organisms/usps/variation-1/v1.0.0/zoe-organism-usps.vue';
// json5-ignore
import StepTestimonials from '../v2.0.0/components/testimonials/step-testimonials.vue';
// json5-ignore
import StepFaq from '../v2.0.0/components/faq/step-faq.vue';
// json5-ignore
import ZoeOrganismDisclaimer from '@organisms/disclaimer/variation-1/v1.0.0/zoe-organism-disclaimer.vue';
import General from '../../../../../resources/frontend/js/mixins/general.vue';
import Page from '../../../../../resources/frontend/js/mixins/page.vue';
import Step from '../../../../resources/mixins/step/step.vue';
import { generateButtonVariables, generateContainerVariables, generateInputVariables } from '../../../../resources/mixins/styling/styling';
import scrollToSelector from '../../../../resources/mixins/utils/scroll-to-selector';
import { goToNextStep, goToPreviousStep } from '../../../../resources/mixins/step/v2/step';
import goToPreviousQuestion from '../../../../resources/mixins/quiz/v2.0.0/go-to-previous-question';

export default {
    components: {
        ZoeAtomMedia,
        ZoeOrganismBanner,
        StepHeader,
        StepInformation,
        StepMediaBlocks,
        StepStartCta,
        StepOverlay,
        StepQuestions,
        StepForm,
        StepHighlightImage,
        StepGallery,
        StepButtonGroupImage,
        ZoeOrganismUsps,
        StepTestimonials,
        StepFaq,
        ZoeOrganismDisclaimer,
    },

    mixins: [General, Page, Step],

    props: {
        step: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            startStep: 1,
            quizStep: 2,
            formStep: 3,
            flowHasStarted: false,
        };
    },

    computed: {
        background() {
            return this.getValue(`${this.step}.style.background`);
        },

        overlayBackground() {
            return this.getValue(`${this.step}.overlay.style.background`);
        },
    },

    methods: {
        generateButtonVariables,
        generateContainerVariables,
        generateInputVariables,
        beforeLeave(el) {
            const parent = el.parentElement;
            parent.style.height = el.clientHeight + 'px';
        },

        onEnter(el) {
            const parent = el.parentElement;

            setTimeout(() => {
                parent.style.height = el.clientHeight + 'px';
            }, 50);

            setTimeout(() => {
                parent.style.height = '';
            }, 350);
        },

        toggleOverlay() {
            if (!this.getValue(`${this.step}.overlay.enable`)) return;

            const questions = this.$store.state.quiz.questionsData;
            const formHasMountedOnce = this.$store.getters['component/getTemplateValue']('form', 'hasMountedOnce');
            const startCtaIsActive = this.getValue(`${this.step}.startCta.show`);

            if (this.flowHasStarted) {
                this.flowHasStarted = !this.flowHasStarted;

                setTimeout(() => {
                    scrollToSelector('.step__flow', -60, () => {
                        if (!startCtaIsActive && questions.length === 1) {
                            return;
                        }

                        if (formHasMountedOnce) {
                            goToPreviousStep(1);
                            return;
                        }

                        goToPreviousStep();
                    });
                }, 500);
            } else {
                scrollToSelector('body', 0, () => {
                    this.flowHasStarted = !this.flowHasStarted;

                    if (startCtaIsActive && formHasMountedOnce) {
                        goToNextStep();
                    }
                });
            }
        },

        previewOverlay() {
            const showQuiz = this.getValue(`${this.step}.quiz.style.show`);

            if (!showQuiz) {
                this.quizStep = 0;
                this.formStep = 1;
                return;
            }

            this.quizStep = 1;
        },
    },

    created() {
        const showStartCta = this.getValue(`${this.step}.startCta.show`);
        const showQuiz = this.getValue(`${this.step}.quiz.style.show`);
        const previewSMSVerification = this.getValue(`${this.step}.form.smsVerification.preview`);

        this.$store.state.page.scrollTarget = '.step__flow';

        if (this.$store.state.page.isPreview && previewSMSVerification) {
            this.startStep = 0;
            this.quizStep = 0;
            this.formStep = 1;
            return;
        }

        if (!showStartCta) {
            this.quizStep = 1;
        }

        if (!showStartCta && !showQuiz) {
            this.quizStep = 0;
            this.formStep = 1;
        }

        if (showStartCta && !showQuiz) {
            this.quizStep = 0;
            this.formStep = 2;
        }
    },
};
</script>

<style lang="scss" scoped>
@use 'main';

@layer step {
    .step {
        --background: v-bind(background);
        --overlay-background: v-bind(overlayBackground);
    }
}

.step-transition-leave-active {
    transition: all .3s ease;
}

.step-transition-enter-active {
    transition: all .3s ease .3s;
}

.step-transition-leave-to,
.step-transition-enter-from {
    position: absolute !important;
    top: 0;
    width: 100%;
    opacity: 0;
}

.form-container-transition-leave-active {
    transition: all .4s ease;
}

.form-container-transition-enter-active {
    transition: all .4s ease .4s;
}

.form-container-transition-leave-to,
.form-container-transition-enter-from {
    opacity: 0;
}
</style>
